import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiDefaults, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { GetSellerInvoiceExportResponse } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { ClaimService, FeatureFlagService } from '@lsq-frontend/common-ui';
import { startWith } from 'rxjs/operators';

import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { GetSellerInvoiceExportRequest } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardFinancingSellerClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    mapBuyerPayableSortColumn,
    mapInvoiceFundingStatusToLegacyStatus,
    PayablesInput
} from '@lsq-frontend/remote-services/financing-graphql';
import { SortDirection } from 'aws-amplify';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { IExportRequest } from '../models/export-request';
import { ExportType } from '../models/export-type';

@Injectable({ providedIn: 'root' })
export class GetSellerInvoiceExportService extends ProtoApiService<
    GetSellerInvoiceExportRequest,
    GetSellerInvoiceExportResponse
> {
    public requestType = GetSellerInvoiceExportRequest;
    public responseType = GetSellerInvoiceExportResponse;
    public endpointName = 'GetSellerInvoiceExport';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService,
        private _claimService: ClaimService,
        private _userService: UserService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v2/financingExport';
                    this.serviceName = 'reportCenter';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'GetSellerInvoiceExport';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetSellerInvoiceExportRequest
    ): IExportRequest | GetSellerInvoiceExportRequest {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const supplierIds = [
                this._claimService.isOnSync(DashboardFinancingSellerClaim.FinancingVendorPortal)
                    ? this._userService.getAlternateCompanyKey()
                    : req.refSellerKey
            ];
            const input: PayablesInput = omitBy(
                {
                    companyFilter: { supplierIds },
                    payableNameSearch: req.invoiceNumber || '',
                    legacyStatuses: mapInvoiceFundingStatusToLegacyStatus(req.fundingStatus),
                    pagination: {
                        skip: skip || 0,
                        take: take || 0,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] ?? SortDirection.ASCENDING
                        )
                    }
                },
                isNil
            );

            return {
                fileType: 'csv',
                exportType: ExportType.SUPPLIER_INVOICES,
                query: `{${jsonToGraphQLQuery({
                    payables: {
                        __args: { input },
                        pagination: {
                            skip: true,
                            take: true,
                            sortColumn: true,
                            sortDirection: true
                        },
                        rows: {
                            id: true,
                            name: true,
                            invoiceDateParent: true,
                            dueInDays: true,
                            amount: { cents: true, currencyCode: true },
                            buyerDiscountFee: { cents: true, currencyCode: true },
                            buyerDiscountFeeRate: true,
                            originalDueDate: true,
                            fundingDate: true,
                            status: true,
                            statusLegacy: true,
                            actualBuyerDueDate: true,
                            fundingAmount: { cents: true, currencyCode: true },
                            amountLessSupplierDiscountFee: { cents: true, currencyCode: true },
                            supplierDiscountFee: { cents: true, currencyCode: true },
                            supplierDiscountFeeRate: true,
                            isGroup: true,
                            nameSearchMatchedNested: true,
                            relationship: {
                                buyer: { id: true, company: { name: true } },
                                supplier: { company: { name: true } }
                                // TODO: Add back when we implement buyer paid fees
                                // scfConfig: {
                                //     isBuyerPaidEarlyPaymentFeePercentageVisible: true,
                                //     buyerPaidEarlyPaymentFeePercentage: true
                                // }
                            },
                            approvals: {
                                id: true,
                                payLaterRequested: true,
                                payLaterAdjustedDueDate: true,
                                fundingAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                supplierDiscountFee: {
                                    cents: true,
                                    currencyCode: true
                                },
                                originalDueDate: true,
                                amount: { cents: true, currencyCode: true },
                                adjustedAmount: { cents: true, currencyCode: true },
                                invoice: {
                                    date: true,
                                    invoiceNumber: true,
                                    amount: { cents: true, currencyCode: true }
                                },
                                creditMemoAllocations: {
                                    id: true,
                                    approvalId: true,
                                    allocatedAmount: {
                                        cents: true,
                                        currencyCode: true
                                    },
                                    creditMemoId: true,
                                    creditMemo: {
                                        creditMemoNumber: true,
                                        date: true,
                                        amount: {
                                            cents: true,
                                            currencyCode: true
                                        },
                                        allocatedAmount: {
                                            cents: true,
                                            currencyCode: true
                                        }
                                    },
                                    allocationType: true
                                }
                            },
                            creditMemos: {
                                id: true,
                                date: true,
                                creditMemoNumber: true,
                                allocatedAmount: { cents: true, currencyCode: true }
                            },
                            transfers: { transferType: true }
                        },
                        totals: { payableCount: true }
                    }
                })}}`
            };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
